import { ApiRoutes } from "consts/enpoints/api";
import { downloadFileApi, fetchApi, FormSerializers } from "infrastructure/api";
import { ID } from "types";
import type {
  AbstractFileEntity,
  IncrementExchangeRates,
  Increments,
  IncrementTaxesResponse,
  ManageFileTaxes,
  TogglePayMerchantPayoutFilesRequest
} from "./types";
import { generateFileName } from "util/support";

export const getMerchantPayoutSummaryFiles = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutSummaryFiles()}${queryString}`);

export const getMerchantPayoutFiles = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutFiles()}${queryString}`);

export const getMerchantPayoutFilesById = (coreId: ID) =>
  fetchApi(ApiRoutes.GetMerchantPayoutFilesById(coreId));

export const getMerchantPayoutCalculations = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutCalculations}${queryString}`);

export const getMerchantPayoutFileTaxCalculations = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutFileTaxCalculations}${queryString}`);

export const createMerchantPayoutFileTaxCalculations = (body: ManageFileTaxes) =>
  fetchApi(ApiRoutes.PostMerchantPayoutFileTaxCalculations(), {
    method: 'POST',
    body: {
      ...body,
      coreId: undefined
    }
  });

export const updateMerchantPayoutFileTaxCalculations = (body: Partial<ManageFileTaxes>) =>
  fetchApi(ApiRoutes.PostMerchantPayoutFileTaxCalculations(body.merchantPayoutFileId), {
    method: 'PATCH',
    body
  });

export const deleteMerchantPayoutFileTaxCalculations = (coreId: string) =>
  fetchApi(ApiRoutes.DeleteMerchantPayoutFileTaxCalculations(coreId), {
    method: 'DELETE'
  });

export const getTransactionsMerchantPayoutIncrementsPivot = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetTransactionsMerchantPayoutIncrementsPivot}${queryString}`);

export const getMerchantPayoutIncrementTaxes = (queryString = '') =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutIncrementTaxes()}${queryString}`);

export const postMerchantPayoutIncrementTaxes = (body: Partial<IncrementTaxesResponse>) =>
  fetchApi(ApiRoutes.PostMerchantPayoutIncrementTaxes, {
    method: "POST",
    body,
  });
export const deleteMerchantPayoutIncrementTaxes = (coreId?: ID) =>
  fetchApi(ApiRoutes.DeleteMerchantPayoutIncrementTaxes(coreId), {
    method: "DELETE",
  });
export const getMerchantPayoutIncrements = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutIncrements}${queryString}`);

export const putMerchantPayoutIncrements = (body: Partial<Increments>) =>
  fetchApi(ApiRoutes.PutMerchantPayoutIncrements(body.coreId!), {
    method: "PUT",
    body
  });

export const getMerchantPayoutIncrementExchangeRates = (queryString: string) =>
  fetchApi(`${ApiRoutes.GetMerchantPayoutIncrementExchangeRates}${queryString}`);

export const postMerchantPayoutIncrementExchangeRates = (body: Partial<IncrementExchangeRates>) =>
  fetchApi(ApiRoutes.PostMerchantPayoutIncrementExchangeRates, {
    method: "POST",
    body,
  });

export const deleteMerchantPayoutIncrementExchangeRates = (coreId?: ID) =>
  fetchApi(ApiRoutes.DeleteMerchantPayoutIncrementExchangeRates(coreId), {
    method: "DELETE",
  });

export const postMerchantPayoutIncrementExchangeRatesBulk = (body: FormData) =>
  fetchApi(ApiRoutes.PostMerchantPayoutIncrementExchangeRatesBulk, {
    serializer: FormSerializers.FormData,
    method: "POST",
    body,
  });

export const postMerchantPayoutIncrementTaxesBulk = (body: FormData) =>
  fetchApi(ApiRoutes.PostMerchantPayoutIncrementTaxesBulk, {
    serializer: FormSerializers.FormData,
    method: "POST",
    body,
  });

export const downloadIncremetExchangeRatesCSVTemplate = (fileName: string) =>
  downloadFileApi(ApiRoutes.PostMerchantPayoutIncrementExchangeRatesBulk, {
    headers: {
      Accept: "text/csv",
    },
    fileName
  });

export const downloadIncremetTaxesCSVTemplate = (fileName: string) =>
  downloadFileApi(ApiRoutes.PostMerchantPayoutIncrementTaxesBulk, {
    headers: {
      Accept: "text/csv",
    },
    fileName
  });

export const postMerchantPayoutFiles = (body: FormData) =>
  fetchApi(ApiRoutes.PostMerchantPayoutFiles, {
    serializer: FormSerializers.FormData,
    method: "POST",
    body,
  });

export const getMerchantPayoutFilesSettlementXLS = ({
  coreId,
  fileKey
}: Pick<AbstractFileEntity, 'coreId' | 'fileKey'>) =>
  downloadFileApi(ApiRoutes.DownloadMerchantPayoutSummaryFiles(coreId, fileKey!), {
    headers: {
      Accept: "application/xls",
    },
    fileName: generateFileName({
      prefix: 'Settlement',
      fileKey,
      extension: 'xls'
    })
  });

export const getMerchantPayoutSummaryFilesTransactionCSV = ({
  coreId,
  fileKey
}: Pick<AbstractFileEntity, 'coreId' | 'fileKey'>) =>
  downloadFileApi(ApiRoutes.GetMerchantPayoutSummaryFilesTransactionsCSV(coreId), {
    headers: {
      Accept: "text/csv"
    },
    fileName: generateFileName({
      prefix: 'Transactions',
      fileKey: [coreId, fileKey].join('_'),
      extension: 'csv'
    })
  });

export const previewMerchantPayoutFilesSettlementXLS = (coreId: ID, fileName: string, MOR: string) =>
  downloadFileApi(ApiRoutes.PreviewMerchantPayoutFilesSettlementXLS(coreId, MOR), {
    headers: {
      Accept: "application/xls",
    },
    fileName
  });
export const getMerchantPayoutFilesTransactionCSV = (coreId: ID, fileName: string) =>
  downloadFileApi(ApiRoutes.GetMerchantPayoutFilesTransactionsCSV(coreId), {
    headers: {
      Accept: "text/csv",
    },
    fileName
  });

export const getMerchantPayoutFilesStillPendingSettlementCSV = (coreId: ID, fileName: string) =>
  downloadFileApi(ApiRoutes.GetMerchantPayoutFilesStillPendingSettlementCSV(coreId), {
    headers: {
      Accept: "text/csv",
    },
    fileName
  });

export const generateMerchantPayoutFilesSettlements = (coreId: ID) =>
  fetchApi(ApiRoutes.GenerateMerchantPayoutFilesSettlements(coreId), {
    method: "PATCH",
    headers: {
      Action: "dispatchExcelGeneration",
    },
  });

export const deleteMerchantPayoutFiles = (coreId: ID) =>
  fetchApi(ApiRoutes.DeleteMerchantPayoutFiles(coreId), {
    method: "DELETE",
  });

export const deleteMerchantPayoutSummaryFiles = (coreId: ID) =>
  fetchApi(ApiRoutes.DeleteMerchantPayoutSummaryFiles(coreId), {
    method: "DELETE",
  });

export const paidMerchantPayoutFiles = ({ coreId, ...body }: TogglePayMerchantPayoutFilesRequest) =>
  fetchApi(ApiRoutes.PaidMerchantPayoutFiles(coreId), {
    serializer: FormSerializers.Json,
    method: "PUT",
    body,
  });

export const mergeMerchantPayoutFiles = (body: FormData, route = ApiRoutes.PostMerchantPayoutFiles) =>
  fetchApi(route, {
    serializer: FormSerializers.FormData,
    method: "POST",
    body,
  });

export const postMerchantPayoutSummaryFiles = (body: FormData) =>
  fetchApi(ApiRoutes.PostMerchantPayoutSummaryFiles, {
    serializer: FormSerializers.FormData,
    method: "POST",
    body,
  });
